import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { buildApiUrl } from "../config/api";

const emailPlans = [
  {
    title: "Starter Email",
    serviceKey: "starter_email",
    price: 149,
    storage: "5GB",
    emailAccounts: 1,
    provider: "Private Email",
    features: ["Webmail Access", "Mobile Sync", "Spam Protection", "Free DNS Guidance"],
  },
  {
    title: "Professional Email",
    serviceKey: "professional_email",
    price: 349,
    storage: "25GB",
    emailAccounts: 10,
    provider: "Zoho Mail",
    features: ["Calendar & Contacts", "Advanced Security", "Cloud Backup", "Priority Support"],
  },
  {
    title: "Enterprise Email",
    serviceKey: "enterprise_email",
    price: 699,
    storage: "100GB",
    emailAccounts: 25,
    provider: "Google Workspace",
    features: ["Admin Dashboard", "Advanced Spam Filtering", "Team Collaboration", "24/7 Support"],
  },
];

const normalizeDomain = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "");

const normalizeMailboxName = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/^mailto:/, "")
    .split("@")[0];

const getDomainFromEmailAddress = (value) => {
  const parts = String(value || "")
    .toLowerCase()
    .trim()
    .replace(/^mailto:/, "")
    .split("@");

  return parts.length === 2 ? normalizeDomain(parts[1]) : "";
};

const isValidDomain = (value) =>
  /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(value);

const isValidMailboxName = (value) =>
  /^(?=.{1,64}$)[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/.test(value) &&
  !value.includes("..");

const Email = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialRequestedAddress = String(location.state?.requestedEmailAddress || "");
  const initialDomain = normalizeDomain(
    location.state?.domainName || getDomainFromEmailAddress(initialRequestedAddress) || ""
  );
  const [domainName, setDomainName] = useState(initialDomain);
  const [mailboxName, setMailboxName] = useState(
    normalizeMailboxName(location.state?.mailboxName || initialRequestedAddress)
  );
  const [domainSource, setDomainSource] = useState(location.state?.domainSource || "external");
  const [dnsCheck, setDnsCheck] = useState(null);
  const [dnsCheckLoading, setDnsCheckLoading] = useState(false);
  const [dnsCheckError, setDnsCheckError] = useState("");
  const [availabilityResult, setAvailabilityResult] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");

  const cleanedDomain = useMemo(() => normalizeDomain(domainName), [domainName]);
  const isMailboxValid = !mailboxName || isValidMailboxName(mailboxName);
  const canCheckDns = isValidDomain(cleanedDomain);
  const canCheckAvailability = Boolean(mailboxName) && canCheckDns && isMailboxValid;
  const canBuy = Boolean(availabilityResult?.isAvailable);
  const requestedEmailAddress =
    mailboxName && cleanedDomain ? `${mailboxName}@${cleanedDomain}` : "";

  const handleMailboxChange = (event) => {
    const value = event.target.value;
    const pastedDomain = getDomainFromEmailAddress(value);

    setMailboxName(normalizeMailboxName(value));
    setAvailabilityResult(null);
    setAvailabilityError("");

    if (pastedDomain) {
      setDomainName(pastedDomain);
    }
  };

  const checkAvailability = async () => {
    if (!canCheckAvailability) {
      setAvailabilityError("Enter a valid mailbox and domain before checking availability.");
      return;
    }

    setAvailabilityLoading(true);
    setAvailabilityResult(null);
    setAvailabilityError("");

    try {
      const params = new URLSearchParams({
        domain: cleanedDomain,
        mailbox: mailboxName,
        domainSource,
      });
      const response = await fetch(buildApiUrl(`/api/email-availability?${params.toString()}`));
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Unable to check mailbox availability.");
      }

      setAvailabilityResult(data);
    } catch (error) {
      setAvailabilityError(error.message);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const startCheckout = (plan) => {
    if (!canBuy) {
      return;
    }

    navigate("/payment/razorpay", {
      state: {
        orderType: "email",
        serviceKey: plan.serviceKey,
        amount: plan.price,
        emailPlan: plan.title,
        provider: plan.provider,
        domainName: cleanedDomain,
        domainSource,
        mailboxName,
        requestedEmailAddress,
        emailAccounts: plan.emailAccounts,
        storage: plan.storage,
      },
    });
  };

  const checkEmailDns = async () => {
    if (!canCheckDns) {
      setDnsCheckError("Enter a valid root domain before checking DNS.");
      return;
    }

    setDnsCheckLoading(true);
    setDnsCheck(null);
    setDnsCheckError("");

    try {
      const response = await fetch(
        buildApiUrl(`/api/email-dns-check?domain=${encodeURIComponent(cleanedDomain)}`)
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Unable to check email DNS.");
      }

      setDnsCheck(data);
    } catch (error) {
      setDnsCheckError(error.message);
    } finally {
      setDnsCheckLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-violet-950 to-slate-900 px-6 py-28 text-white">
      <div className="absolute left-10 top-20 h-72 w-72 rounded-full bg-violet-500/20 blur-3xl"></div>
      <div className="absolute bottom-10 right-10 h-96 w-96 rounded-full bg-fuchsia-500/20 blur-3xl"></div>

      <div className="mx-auto max-w-7xl">
        <div className="grid items-center gap-20 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -80 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl font-black leading-tight md:text-7xl">
              Professional
              <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                {" "}Business Email
              </span>
            </h1>

            <p className="mt-8 text-lg leading-8 text-slate-300">
              Sell email as a managed setup, not only a mailbox. We verify the domain,
              configure MX, SPF, DKIM, and DMARC records, then activate mailboxes.
            </p>

            <div className="mt-10 flex flex-wrap gap-5">
              <a
                href="#email-plans"
                className="rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-8 py-4 font-bold transition-all duration-300 hover:scale-105"
              >
                Get Business Email
              </a>

              <a
                href="#domain-source"
                className="rounded-2xl border border-white/20 bg-white/10 px-8 py-4 font-bold backdrop-blur-xl transition-all duration-300 hover:bg-white/20"
              >
                Domain Setup
              </a>
            </div>

            <div className="mt-16 grid grid-cols-2 gap-5">
              {[
                "Domain Verification",
                "MX Routing",
                "SPF + DKIM",
                "DMARC Monitoring",
                "Mailbox Setup",
                "Admin Handover",
              ].map((item) => (
                <div
                  key={item}
                  className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-xl"
                >
                  {item}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
            className="rounded-[40px] border border-white/10 bg-white/10 p-8 backdrop-blur-xl"
          >
            <div className="rounded-3xl bg-slate-900 p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Email Setup Status</h2>
                <div className="rounded-full bg-green-500 px-4 py-1 text-sm font-bold">
                  Ready
                </div>
              </div>

              <div className="mt-8 space-y-5">
                {[
                  ["Ownership", "TXT or CNAME verification"],
                  ["Delivery", "MX records point to provider"],
                  ["Trust", "SPF, DKIM, and DMARC enabled"],
                ].map(([title, detail]) => (
                  <div key={title} className="rounded-2xl bg-white/10 p-5">
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="font-bold">{title}</h3>
                      <span className="text-green-400">Required</span>
                    </div>
                    <p className="mt-3 text-sm text-slate-300">{detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        <section id="domain-source" className="mt-24 rounded-[35px] border border-white/10 bg-white/10 p-8 backdrop-blur-xl">
          <h2 className="text-3xl font-black md:text-5xl">Choose Domain Source</h2>
          <p className="mt-4 max-w-4xl text-slate-300">
            Email setup is different depending on who controls DNS. If Asiduo manages the
            domain, we update records directly. If the domain is external, we create the
            provider account and send exact DNS records for the customer to add.
          </p>

          <div className="mt-8 grid gap-4 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)_auto_auto]">
            <div>
              <label className="mb-2 block text-sm font-bold text-slate-200">
                Desired Mailbox
              </label>

              <div className="flex h-14 items-center rounded-2xl border border-white/10 bg-white/10 px-5 focus-within:border-fuchsia-400">
                <input
                  type="text"
                  value={mailboxName}
                  onChange={handleMailboxChange}
                  placeholder="sravanthi"
                  className="min-w-0 flex-1 bg-transparent text-white outline-none placeholder:text-slate-400"
                />
                <span className="ml-2 text-slate-400">@</span>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-bold text-slate-200">
                Domain
              </label>

              <input
                type="text"
                value={domainName}
                onChange={(event) => {
                  setDomainName(event.target.value);
                  setAvailabilityResult(null);
                  setAvailabilityError("");
                }}
                placeholder="asiduo.com"
                className="h-14 w-full rounded-2xl border border-white/10 bg-white/10 px-5 text-white outline-none placeholder:text-slate-400 focus:border-fuchsia-400"
              />
            </div>

            <button
              type="button"
              onClick={() => {
                setDomainSource("asiduo_registered");
                setAvailabilityResult(null);
                setAvailabilityError("");
              }}
              className={`h-14 rounded-2xl px-5 text-sm font-bold lg:mt-7 ${
                domainSource === "asiduo_registered"
                  ? "bg-fuchsia-500 text-white"
                  : "border border-white/20 bg-white/10 text-white"
              }`}
            >
              Bought From Us
            </button>

            <button
              type="button"
              onClick={() => {
                setDomainSource("external");
                setAvailabilityResult(null);
                setAvailabilityError("");
              }}
              className={`h-14 rounded-2xl px-5 text-sm font-bold lg:mt-7 ${
                domainSource === "external"
                  ? "bg-fuchsia-500 text-white"
                  : "border border-white/20 bg-white/10 text-white"
              }`}
            >
              External Domain
            </button>
          </div>

          {!canCheckDns && domainName ? (
            <p className="mt-4 text-sm font-semibold text-rose-300">
              Enter a valid root domain such as example.com.
            </p>
          ) : null}

          {mailboxName && !isMailboxValid ? (
            <p className="mt-4 text-sm font-semibold text-rose-300">
              Mailbox can use letters, numbers, dot, underscore, or hyphen, and must start and end with a letter or number.
            </p>
          ) : null}

          {requestedEmailAddress && isMailboxValid && canCheckDns ? (
            <p className="mt-4 text-sm font-semibold text-emerald-300">
              Requested email address: {requestedEmailAddress}
            </p>
          ) : null}

          <div className="mt-6 flex flex-wrap gap-4">
            <button
              type="button"
              onClick={checkAvailability}
              disabled={!canCheckAvailability || availabilityLoading}
              className="rounded-2xl border border-emerald-300/50 bg-emerald-500/15 px-6 py-3 text-sm font-bold text-white transition hover:bg-emerald-500/25 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {availabilityLoading ? "Checking Availability..." : "Check Email Availability"}
            </button>

            <button
              type="button"
              onClick={checkEmailDns}
              disabled={!canCheckDns || dnsCheckLoading}
              className="rounded-2xl border border-fuchsia-300/50 bg-fuchsia-500/15 px-6 py-3 text-sm font-bold text-white transition hover:bg-fuchsia-500/25 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {dnsCheckLoading ? "Checking DNS..." : "Check Email DNS"}
            </button>
          </div>

          {availabilityError ? (
            <p className="mt-4 text-sm font-semibold text-rose-300">{availabilityError}</p>
          ) : null}

          {availabilityResult ? (
            <div
              className={`mt-4 rounded-2xl border p-4 text-sm ${
                availabilityResult.isAvailable
                  ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200"
                  : "border-amber-400/30 bg-amber-500/10 text-amber-200"
              }`}
            >
              <p className="font-semibold">
                {availabilityResult.isAvailable
                  ? "This mailbox is available."
                  : "This mailbox is currently unavailable."}
              </p>
              {availabilityResult.reason ? <p className="mt-2">{availabilityResult.reason}</p> : null}

              {availabilityResult.suggestedAlternatives?.length ? (
                <p className="mt-2 text-xs text-slate-300">
                  Suggestions: {availabilityResult.suggestedAlternatives.join(", ")}
                </p>
              ) : null}
            </div>
          ) : null}

          {dnsCheckError ? (
            <p className="mt-4 text-sm font-semibold text-rose-300">{dnsCheckError}</p>
          ) : null}

          {dnsCheck ? (
            <div className="mt-6 grid gap-4 lg:grid-cols-3">
              {[
                ["MX Records", dnsCheck.status.hasMx, "Inbound mail routing"],
                ["SPF Record", dnsCheck.status.hasSpf, "Outbound sender policy"],
                ["DMARC Record", dnsCheck.status.hasDmarc, "Domain mail protection"],
              ].map(([label, ready, detail]) => (
                <div
                  key={label}
                  className={`rounded-2xl border p-5 ${
                    ready
                      ? "border-emerald-400/30 bg-emerald-500/10"
                      : "border-amber-400/30 bg-amber-500/10"
                  }`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="font-bold text-white">{label}</h3>
                    <span className={ready ? "text-emerald-300" : "text-amber-300"}>
                      {ready ? "Found" : "Needed"}
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-slate-300">{detail}</p>
                </div>
              ))}

              <div className="rounded-2xl border border-white/10 bg-slate-950/50 p-5 lg:col-span-3">
                <h3 className="font-bold text-fuchsia-300">Current Mail Records</h3>
                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-300">MX</p>
                    <div className="mt-2 space-y-2 text-sm text-slate-400">
                      {dnsCheck.records.mx.length ? (
                        dnsCheck.records.mx.map((record) => (
                          <p key={`${record.exchange}-${record.priority}`}>
                            {record.priority} {record.exchange}
                          </p>
                        ))
                      ) : (
                        <p>No MX records found.</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-slate-300">SPF</p>
                    <div className="mt-2 space-y-2 break-words text-sm text-slate-400">
                      {dnsCheck.records.spf.length ? (
                        dnsCheck.records.spf.map((record) => <p key={record}>{record}</p>)
                      ) : (
                        <p>No SPF record found.</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-slate-300">DMARC</p>
                    <div className="mt-2 space-y-2 break-words text-sm text-slate-400">
                      {dnsCheck.records.dmarc.length ? (
                        dnsCheck.records.dmarc.map((record) => <p key={record}>{record}</p>)
                      ) : (
                        <p>No DMARC record found.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </section>

        <section id="email-plans" className="mt-24">
          <h2 className="text-center text-4xl font-black md:text-6xl">Email Plans</h2>

          <div className="mt-20 grid gap-10 md:grid-cols-3">
            {emailPlans.map((plan) => (
              <motion.div
                key={plan.serviceKey}
                whileHover={{ scale: 1.05, y: -10 }}
                className="rounded-[35px] border border-white/10 bg-white/10 p-10 backdrop-blur-xl"
              >
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-fuchsia-300">
                  {plan.provider}
                </p>

                <h3 className="mt-4 text-3xl font-black">{plan.title}</h3>

                <p className="mt-6 text-5xl font-black text-fuchsia-400">
                  Rs. {plan.price}
                  <span className="text-base font-semibold text-slate-300">/mo</span>
                </p>

                <div className="mt-8 space-y-4 text-slate-300">
                  <p>{plan.storage} Storage</p>
                  <p>{plan.emailAccounts} Email Account{plan.emailAccounts > 1 ? "s" : ""}</p>
                  {plan.features.map((feature) => (
                    <p key={feature}>{feature}</p>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => startCheckout(plan)}
                  disabled={!canBuy}
                  className="mt-10 w-full rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 py-4 font-bold transition-all duration-300 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {canBuy ? "Buy Now" : "Check Availability First"}
                </button>
              </motion.div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Email;
